import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Alert, Button, Empty, Input, Modal, Popconfirm, Skeleton, Tag, message } from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileZipOutlined,
  LineChartOutlined,
  PaperClipOutlined,
  UploadOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  PlayCircleOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';

import { aiApi, attachmentsApi, lmsAdminApi } from '../lib/wikiApi';
import { kbApi, type CourseOutline, type OutlineLessonType } from '../lib/api/kb';
import { diskTrashedMessage } from '../lib/disk-trash';
import { Button as DsButton, Tag as DsTag } from './ds';

type Attachment = {
  id: string;
  kind: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  sortOrder: number;
  isInline: boolean;
};

function iconByKind(kind: string): JSX.Element {
  switch (kind) {
    case 'IMAGE':
      return <FileImageOutlined style={{ color: '#0096FF' }} />;
    case 'PDF':
      return <FilePdfOutlined style={{ color: '#E53935' }} />;
    case 'XLS':
      return <FileExcelOutlined style={{ color: '#7CB342' }} />;
    case 'ZIP':
      return <FileZipOutlined style={{ color: '#9C5BD4' }} />;
    default:
      return <FileTextOutlined style={{ color: '#251D4A' }} />;
  }
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

/**
 * Секция вложений статьи (Q29-32). Список файлов со скачиванием; для
 * редакторов — загрузка и удаление. Видео (S3) пока не поддерживается.
 */
export function AttachmentsSection({
  slug,
  articleId,
  attachments,
  canEdit,
}: {
  slug: string;
  articleId: string;
  attachments: Attachment[];
  canEdit: boolean;
}): JSX.Element | null {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  // Прогресс параллельной загрузки (для больших файлов), 0–100 или null.
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  // «Вложения» = прикреплённые к статье файлы (PDF/SVG/картинки-приложения и
  // т.д.). Картинки, вставленные inline в текст из редактора (isInline=true),
  // НЕ показываем — чтобы не дублировать встроенный контент. Отдельно
  // загруженные файлы (включая картинки/SVG через кнопку «Загрузить») видны.
  const files = attachments.filter((a) => !a.isInline);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      setUploadPct(0);
      return attachmentsApi.uploadParallel(articleId, file, (p) => setUploadPct(p));
    },
    onSuccess: async () => {
      setUploadPct(null);
      message.success('Файл загружен');
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => {
      setUploadPct(null);
      message.error(e.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => attachmentsApi.delete(id),
    onSuccess: async (r) => {
      // Копия вложения на Диске ушла в Корзину вслед за вложением — говорим об этом (30 дней на возврат).
      message.success(diskTrashedMessage(r.diskTrashed) ?? 'Удалено', r.diskTrashed?.length ? 6 : undefined);
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // Переименование: то же имя получает копия вложения на Диске.
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);
  const renameMutation = useMutation({
    mutationFn: (v: { id: string; name: string }) => attachmentsApi.rename(v.id, v.name),
    onSuccess: async () => {
      message.success('Переименовано — и на Диске тоже');
      setRenameTarget(null);
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // «Скрыть из вложений» — помечает вложение как inline (остаётся в тексте
  // статьи, но исчезает из панели «Вложения»). Для старых картинок, которые
  // были загружены до разделения inline/файл.
  const hideMutation = useMutation({
    mutationFn: (id: string) => attachmentsApi.setInline(id, true),
    onSuccess: async () => {
      message.success('Скрыто из вложений (осталось в тексте статьи)');
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!canEdit && files.length === 0) return null;

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <PaperClipOutlined style={{ color: 'var(--text-muted)' }} />
        <h3 style={{ fontSize: 15, margin: 0 }}>Вложения</h3>
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadMutation.mutate(f);
                e.target.value = '';
              }}
            />
            <Button
              size="small"
              icon={<UploadOutlined />}
              loading={uploadMutation.isPending}
              onClick={() => inputRef.current?.click()}
              style={{ marginLeft: 'auto' }}
            >
              {uploadMutation.isPending && uploadPct !== null ? `Загрузка ${uploadPct}%` : 'Загрузить'}
            </Button>
          </>
        )}
      </div>

      {files.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Файлов нет. Поддерживаются PDF, Word, Excel, PPT, AI, SVG, картинки, ZIP и др. (до 1 ГБ).
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {files.map((a) => (
            <div
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: 'var(--bg)',
              }}
            >
              {a.kind === 'IMAGE' ? (
                <img
                  src={attachmentsApi.downloadUrl(a.id)}
                  alt=""
                  style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)', flexShrink: 0, background: '#fff' }}
                />
              ) : (
                <span style={{ fontSize: 18 }}>{iconByKind(a.kind)}</span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Клик по названию — открыть/просмотреть файл (inline в новой вкладке). */}
                <a
                  href={attachmentsApi.downloadUrl(a.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Открыть файл"
                  style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'inherit',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a.fileName}
                </a>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {a.kind} · {fmtSize(a.fileSize)}
                </div>
              </div>
              {/* Кнопка «Скачать» — принудительное сохранение (download-атрибут
                  same-origin форсит диалог «куда сохранить», а не открытие в браузере). */}
              <Button
                size="small"
                icon={<DownloadOutlined />}
                href={attachmentsApi.downloadUrl(a.id)}
                download={a.fileName}
              >
                Скачать
              </Button>
              {canEdit && a.kind === 'IMAGE' && (
                <Tooltip title="Скрыть из вложений (картинка останется в тексте статьи)">
                  <Popconfirm
                    title="Скрыть из вложений?"
                    description="Картинка останется в тексте статьи, но пропадёт из списка «Вложения»."
                    onConfirm={() => hideMutation.mutate(a.id)}
                    okText="Скрыть"
                    cancelText="Отмена"
                  >
                    <Button size="small" icon={<EyeInvisibleOutlined />} loading={hideMutation.isPending} />
                  </Popconfirm>
                </Tooltip>
              )}
              {canEdit && (
                <Tooltip title="Переименовать (копия на Диске получит то же имя)">
                  <Button size="small" icon={<EditOutlined />} aria-label={`Переименовать «${a.fileName}»`} onClick={() => setRenameTarget({ id: a.id, name: a.fileName })} />
                </Tooltip>
              )}
              {canEdit && (
                <Popconfirm
                  title="Удалить вложение?"
                  description="Копия файла на Диске уйдёт в Корзину на 30 дней, если ею больше не пользуются."
                  onConfirm={() => deleteMutation.mutate(a.id)}
                  okText="Удалить"
                  cancelText="Отмена"
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            </div>
          ))}
        </div>
      )}
      <Modal
        open={!!renameTarget}
        title="Переименовать вложение"
        okText="Сохранить"
        cancelText="Отмена"
        okButtonProps={{ disabled: !renameTarget?.name.trim(), loading: renameMutation.isPending }}
        onCancel={() => setRenameTarget(null)}
        onOk={() => renameTarget && renameMutation.mutate({ id: renameTarget.id, name: renameTarget.name.trim() })}
        destroyOnHidden
      >
        <Input
          autoFocus
          maxLength={200}
          value={renameTarget?.name ?? ''}
          onChange={(e) => setRenameTarget((t) => (t ? { ...t, name: e.target.value } : t))}
          onPressEnter={() => renameTarget?.name.trim() && renameMutation.mutate({ id: renameTarget.id, name: renameTarget.name.trim() })}
        />
      </Modal>
    </div>
  );
}

/**
 * Виджет связи SOP ↔ KPI (Q73). Если у статьи задан linked_kpi_code —
 * показываем кнопку открыть операционный дашборд CRM по этому показателю.
 */
export function KpiLinkWidget({ kpiCode }: { kpiCode: string }): JSX.Element {
  // Дашборд живёт в CRM (crm.brasol.ru), wiki — на wiki.brasol.ru.
  const dashboardUrl = `https://crm.brasol.ru/dashboard/operational?kpi=${encodeURIComponent(kpiCode)}`;
  return (
    <div
      style={{
        marginTop: 28,
        padding: 16,
        borderRadius: 6,
        border: '1px solid var(--brasol-info)',
        background: 'rgba(0,150,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <LineChartOutlined style={{ fontSize: 22, color: 'var(--brasol-info)' }} />
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700 }}>Связанный показатель</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Этот регламент влияет на KPI <Tag color="blue" style={{ margin: 0 }}>{kpiCode}</Tag> в операционном дашборде.
        </div>
      </div>
      <Button type="primary" href={dashboardUrl} target="_blank" rel="noopener noreferrer" style={{ background: 'var(--brasol-info)', borderColor: 'var(--brasol-info)' }}>
        Открыть в дашборде
      </Button>
    </div>
  );
}

// ─── HUB 2.0 (W2): «Сделать курсом» и «Проверить на противоречия» ──────

const OUTLINE_TYPE: Record<OutlineLessonType, { label: string; icon: JSX.Element }> = {
  article: { label: 'Теория', icon: <ReadOutlined /> },
  video: { label: 'Видео', icon: <PlayCircleOutlined /> },
  quiz: { label: 'Тест', icon: <CheckSquareOutlined /> },
  practical: { label: 'Практика', icon: <ExperimentOutlined /> },
};

/**
 * «Сделать курсом»: AI (POST /wiki/ai/course-outline) раскладывает статью на
 * модули и уроки. УК (kb:admin) может сразу создать черновик курса в
 * конструкторе (курс DRAFT + модули + пустые уроки, статья привязана к
 * первому модулю и первому уроку-теории). Без AI — план по заголовкам статьи.
 */
export function CourseOutlineModal({
  articleSlug,
  articleId,
  canCreateCourse,
  onClose,
}: {
  articleSlug: string;
  articleId: string;
  canCreateCourse: boolean;
  onClose: () => void;
}): JSX.Element {
  const q = useQuery({
    queryKey: ['wiki', 'ai', 'course-outline', articleSlug],
    queryFn: () => kbApi.courseOutline({ articleSlug }),
    retry: false,
    staleTime: Infinity,
  });
  const outline = q.data;
  const [createdId, setCreatedId] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: async (o: CourseOutline): Promise<string> => {
      const { id } = await lmsAdminApi.createCourse({ name: o.title || 'Новый курс', description: o.description || undefined });
      let linked = false;
      for (const [i, m] of o.modules.entries()) {
        const mod = await lmsAdminApi.addModule(id, { title: m.title, articleId: i === 0 ? articleId : null });
        for (const l of m.lessons) {
          const lesson = await lmsAdminApi.addLesson(mod.id, { title: l.title, type: l.type });
          if (!linked && l.type === 'article') {
            linked = true;
            await lmsAdminApi.updateLesson(lesson.id, { articleId, estimatedMinutes: l.min });
          }
        }
      }
      return id;
    },
    onSuccess: (id) => {
      setCreatedId(id);
      message.success('Черновик курса создан');
    },
    onError: (e: Error) => message.error(`Не удалось создать курс: ${e.message}`),
  });
  const totalMin = outline ? outline.modules.reduce((s, m) => s + m.lessons.reduce((x, l) => x + l.min, 0), 0) : 0;
  return (
    <Modal open title="Сделать курсом" onCancel={onClose} footer={null} width={640} destroyOnHidden>
      {q.isLoading ? (
        <>
          <div style={{ fontSize: 13, color: 'var(--fk-text-muted)', marginBottom: 12 }}>AI раскладывает статью на модули и уроки…</div>
          <Skeleton active paragraph={{ rows: 6 }} />
        </>
      ) : q.error ? (
        <Alert type="error" showIcon message={(q.error as Error).message} />
      ) : outline ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!outline.aiUsed && (
            <Alert type="info" showIcon message="AI сейчас недоступен — план собран по заголовкам статьи. Его можно поправить в конструкторе." />
          )}
          <div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>{outline.title}</div>
            {outline.description && <div style={{ fontSize: 13, color: 'var(--fk-text-2)', marginTop: 2 }}>{outline.description}</div>}
            <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 4 }}>
              {outline.modules.length} мод. · {outline.modules.reduce((s, m) => s + m.lessons.length, 0)} уроков · ≈ {totalMin} мин
            </div>
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {outline.modules.map((m, i) => (
              <div key={i} className="fk-kb-outline-mod">
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                  {i + 1}. {m.title}
                </div>
                {m.lessons.map((l, j) => (
                  <div key={j} className="fk-kb-outline-lesson">
                    <span style={{ color: 'var(--fk-orange)', display: 'inline-flex' }}>{OUTLINE_TYPE[l.type].icon}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>{l.title}</span>
                    <DsTag borderless>{OUTLINE_TYPE[l.type].label}</DsTag>
                    <span style={{ color: 'var(--fk-text-muted)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 3, minWidth: 48, justifyContent: 'flex-end' }}>
                      <ClockCircleOutlined /> {l.min}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {createdId ? (
            <Alert
              type="success"
              showIcon
              message="Черновик курса создан"
              description={
                <span>
                  Допишите уроки и тест, затем назначьте курс.{' '}
                  <Link to={`/admin/courses?course=${encodeURIComponent(createdId)}`} onClick={onClose}>
                    Открыть конструктор →
                  </Link>
                </span>
              }
            />
          ) : (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <DsButton variant="ghost" onClick={onClose}>
                Закрыть
              </DsButton>
              {canCreateCourse ? (
                <DsButton variant="primary" loading={create.isPending} onClick={() => create.mutate(outline)}>
                  Создать черновик курса
                </DsButton>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', alignSelf: 'center' }}>Создать курс может администратор базы знаний</span>
              )}
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * «Проверить на противоречия» (тулбар редактора): AI сверяет стандарты одной
 * категории (GET /wiki/ai/stale-check, kb:admin) — показываем пары с этой
 * статьёй и её просрочку. Проверка может идти до минуты.
 */
export function ConflictsModal({ slug, onClose }: { slug: string; onClose: () => void }): JSX.Element {
  const q = useQuery({ queryKey: ['wiki', 'ai', 'stale-check'], queryFn: () => aiApi.staleCheck(), retry: false, staleTime: 5 * 60_000 });
  const d = q.data;
  const mine = (d?.conflicts ?? []).filter((c) => c.a.slug === slug || c.b.slug === slug);
  const others = (d?.conflicts ?? []).length - mine.length;
  const overdue = d?.overdue.find((r) => r.slug === slug);
  return (
    <Modal open title="Проверка на противоречия" onCancel={onClose} footer={null} width={640} destroyOnHidden>
      {q.isLoading ? (
        <>
          <div style={{ fontSize: 13, color: 'var(--fk-text-muted)', marginBottom: 12 }}>AI сверяет стандарты одной категории — до минуты…</div>
          <Skeleton active paragraph={{ rows: 4 }} />
        </>
      ) : q.error ? (
        <Alert type="error" showIcon message={(q.error as Error).message} />
      ) : d ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!d.aiUsed && <Alert type="warning" showIcon message="AI-провайдер не настроен — противоречия не проверялись." />}
          {overdue && <Alert type="warning" showIcon message="Срок проверки актуальности этой статьи истёк" />}
          {mine.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={d.aiUsed ? 'Противоречий с другими стандартами не найдено' : 'Нет данных'} />
          ) : (
            mine.map((c, i) => {
              const other = c.a.slug === slug ? c.b : c.a;
              return (
                <div key={i} style={{ border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', padding: 12 }}>
                  <div style={{ fontWeight: 600 }}>
                    Расходится с <Link to={`/article/${other.slug}`} target="_blank">{other.title}</Link>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--fk-error)', marginTop: 4 }}>{c.details}</div>
                  <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 4 }}>Категория: {c.category}</div>
                </div>
              );
            })
          )}
          <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>
            Проверено пар стандартов: {d.checkedPairs}
            {others > 0 ? ` · ещё ${others} противоречий в других статьях — вкладка «AI-проверки» в администрировании` : ''}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
